<template>
  <v-autocomplete
    v-model="selectedVal"
    :items="items"
    :item-value="itemValue"
    :item-text="itemText"
    :loading="loading"
    :search-input.sync="search"
    :label="label"
    :placeholder="placeholder"
    :prepend-icon="prependIcon"
    :required="required"
    :rules="rules"
    :return-object="returnObject"
    :hint="hint"
    :persistent-hint="persistentHint"
    :multiple="multiple"
    :chips="chips"
    :disabled="disabled"
    :error-messages="errorMessages"
    :error-count="errorCount"
    cache-items
    @change="handleChange"
    @focus="$emit('focus')"
    @blur="$emit('blur')"
  >
    <template #append-outer>
      <slot name="append-outer" />
    </template>
  </v-autocomplete>
</template>

<script>
import debounce from '@/util/debounce';

export default {
  name: 'DebouncedSelect',
  props: {
    value: {
      type: [String, Number, Object, Array],
      default: null,
    },
    service: {
      type: [String, Function],
      required: true,
    },
    serviceParams: {
      type: Object,
      default: () => {},
    },
    pageSize: {
      type: Number,
      default: 50,
    },
    itemValue: {
      type: [String, Function],
      default: 'id',
    },
    itemText: {
      type: [String, Function],
      required: true,
    },
    initialItem: {
      type: [Object, Array],
      required: false,
      default: null,
    },
    label: {
      type: String,
      default: null,
    },
    placeholder: {
      type: String,
      default: null,
    },
    prependIcon: {
      type: String,
      default: null,
    },
    required: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: Array,
      default: () => [],
    },
    returnObject: {
      type: Boolean,
      default: false,
    },
    hint: {
      type: String,
      default: '',
    },
    persistentHint: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    chips: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    autoSelect: {
      type: Boolean,
      default: false,
    },
    errorMessages: {
      type: Array,
      default: () => [],
    },
    errorCount: {
      type: Number,
      default: 0,
    },
  },
  data: () => ({
    selectedVal: null,
    items: [],
    search: null,
    loading: false,
    initialItems: [],
    allFetched: false,
  }),
  computed: {
    serviceHandler() {
      if (typeof this.service === 'function') {
        return this.service;
      }
      const keys = this.service.split('.');
      let handler = this.$service;
      keys.forEach((k) => {
        handler = handler[k];
      });
      return handler.list;
    },
    itemValueHandler() {
      return this.itemValue || 'id';
    },
  },
  watch: {
    search(val) {
      if (this.allFetched || (this.selectedVal && !this.multiple)) {
        return;
      }
      if (val) {
        this.debouncedSearch(val);
      } else {
        this.items = this.initialItems;
      }
    },
    value(val) {
      if (!val || (val instanceof Array && !val.length)) {
        this.selectedVal = null;
      }
    },
  },
  created() {
    this.debouncedSearch = debounce(this.handleSearch);
    if (this.initialItem) {
      if (this.initialItem instanceof Array) {
        this.items.push(...this.initialItem);
      } else {
        this.items.push(this.initialItem);
      }
      this.selectedVal = this.initialItem;
    }
    this.getInitialItems();
  },
  methods: {
    handleChange(val) {
      this.$emit('input', val);
    },
    async getInitialItems() {
      const params = { page_size: this.pageSize, ...this.serviceParams };
      const resp = await this.serviceHandler({ params });
      this.items = resp.data.results;
      if (resp.data.count <= this.pageSize) {
        this.allFetched = true;
      } else {
        this.initialItems = resp.data.results;
      }
      if (this.autoSelect && this.items.length === 1) {
        this.selectedVal = this.returnObject ? this.items[0] : this.items[0][this.itemValue];
        this.$emit('input', this.selectedVal);
      }
    },
    async handleSearch(val) {
      this.loading = true;
      const params = { search: val, page_size: this.pageSize, ...this.serviceParams };
      const resp = await this.serviceHandler({ params });
      this.items = resp.data.results;
      this.loading = false;
    },
  },
};
</script>
